import { divisionClosedReasonText } from "./divisionRegistrationState";
import { divisionBirthYearRange } from "./tournamentDivisions";

// Lógica pura del selector multi-categoría del link público de inscripción:
// elegibilidad por categoría, parseo de costos y suma de la orden.

export type PublicCategoryLike = {
  id: number;
  name: string;
  gender?: string | null;
  age_min?: number | null;
  age_max?: number | null;
  registration_cost?: string | null;
  // Congelado que manda el backend ("700.00"); el parseo local es fallback
  // para payloads viejos cacheados por la PWA.
  effective_cost?: string | null;
  is_full?: boolean;
  /** Cerrada por fecha/inicio/cupo (cierre por categoría, 2026-10-24). */
  is_closed?: boolean;
  closed_reason?: string | null;
  effective_registration_deadline?: string | null;
  has_own_cost?: boolean;
};

export type PublicFeeOptionLike = {
  category: string;
  label: string;
  amount: string;
};

export const FEE_CATEGORY_LABELS: Record<string, string> = {
  general: "General",
  socio: "Socio",
  profesor: "Profesor",
};

export type FeeLabelOverrides = {
  member_fee_label?: string | null;
  coach_fee_label?: string | null;
} | null | undefined;

/** Etiqueta visible del tipo de costo. `overrides` son las etiquetas que el
 * organizador escribió para ese torneo (p. ej. "Afiliados a la Federación"). */
export function feeCategoryLabel(category: string | null | undefined, overrides?: FeeLabelOverrides): string {
  if (!category) return FEE_CATEGORY_LABELS.general;
  const custom =
    category === "socio" ? overrides?.member_fee_label : category === "profesor" ? overrides?.coach_fee_label : null;
  return custom?.trim() || FEE_CATEGORY_LABELS[category] || category;
}

// true = la división trae costo propio y NO cambia con el tipo de
// inscripción. El flag viene del backend; el parseo local es fallback
// para payloads viejos cacheados por la PWA.
export function categoryHasOwnCost(
  category: Pick<PublicCategoryLike, "registration_cost" | "has_own_cost">,
): boolean {
  if (typeof category.has_own_cost === "boolean") return category.has_own_cost;
  return parseRegistrationCost(category.registration_cost) > 0;
}

export function birthYearFromBirthdate(birthdate: string): number | null {
  if (!birthdate) return null;
  const born = new Date(`${birthdate}T00:00:00`);
  if (Number.isNaN(born.getTime())) return null;
  return born.getFullYear();
}

// Espejo de _parse_registration_cost del backend: conserva dígitos y puntos
// ("$1,200.50 MXN" → 1200.5); ilegible = 0.
export function parseRegistrationCost(value: string | null | undefined): number {
  if (!value) return 0;
  const cleaned = String(value).replace(/[^0-9.]/g, "");
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function effectiveCategoryCost(
  category: Pick<PublicCategoryLike, "registration_cost" | "effective_cost" | "has_own_cost">,
  entryFeeAmount: string | number | null | undefined,
  feeOption?: PublicFeeOptionLike | null,
): number {
  // Tipo declarado distinto de general: sustituye el costo BASE, pero
  // solo si la división no trae costo propio (ese manda sin descuento).
  if (
    feeOption &&
    feeOption.category !== "general" &&
    !categoryHasOwnCost(category)
  ) {
    const fromOption = Number(feeOption.amount);
    if (Number.isFinite(fromOption)) return fromOption;
  }
  if (category.effective_cost != null && category.effective_cost !== "") {
    const fromApi = Number(category.effective_cost);
    if (Number.isFinite(fromApi)) return fromApi;
  }
  const specific = parseRegistrationCost(category.registration_cost);
  if (specific > 0) return specific;
  const general = typeof entryFeeAmount === "number" ? entryFeeAmount : Number(entryFeeAmount ?? 0);
  return Number.isFinite(general) ? general : 0;
}

/** Valida el «Costo por categoría adicional» que escribe el organizador.
 * Vacío = sin regla (null). Acepta «300», «300.50», «$1,200» o «400 MXN».
 * 0 es válido solo si se escribe (adicionales gratis). Rechaza negativos,
 * texto, más de 2 decimales y montos absurdos (> $1,000,000). */
export function parseAdditionalCategoryFee(
  raw: string,
): { ok: true; value: string | null } | { ok: false; error: string } {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: true, value: null };
  const cleaned = trimmed.replace(/^\$\s*/, "").replace(/\s*MXN$/i, "").replace(/,(?=\d{3}(\D|$))/g, "").trim();
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return {
      ok: false,
      error: "Costo por categoría adicional: escribe un monto válido en pesos (por ejemplo 300 o 300.50), sin signo de menos.",
    };
  }
  const amount = Number(cleaned);
  if (!Number.isFinite(amount) || amount > 1_000_000) {
    return { ok: false, error: "Costo por categoría adicional: el monto máximo es $1,000,000." };
  }
  return { ok: true, value: amount.toFixed(2) };
}

export type PricedCategory = { id: number; amount: number; isAdditional: boolean };

/** Espejo de plan_additional_categories del backend (el servidor manda;
 * esto solo informa al jugador). Sin costo adicional: precio normal. Con
 * inscripción base previa: todas las elegidas son adicionales. Sin base: la
 * de MAYOR precio normal es la base (empate: menor id) y las demás
 * adicionales. El adicional nunca cobra más que el precio normal. */
export function priceSelectedCategories(
  categories: Array<Pick<PublicCategoryLike, "id" | "registration_cost" | "effective_cost" | "has_own_cost">>,
  selectedIds: number[],
  entryFeeAmount: string | number | null | undefined,
  feeOption: PublicFeeOptionLike | null | undefined,
  additionalFeeAmount: string | number | null | undefined,
  hasPriorBase: boolean,
): PricedCategory[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const bases = selectedIds
    .map((id) => byId.get(id))
    .filter((category): category is NonNullable<typeof category> => Boolean(category))
    .map((category) => ({ id: category.id, amount: effectiveCategoryCost(category, entryFeeAmount, feeOption) }));
  const extra =
    additionalFeeAmount === null || additionalFeeAmount === undefined || additionalFeeAmount === ""
      ? null
      : Number(additionalFeeAmount);
  if (extra === null || !Number.isFinite(extra)) {
    return bases.map((row) => ({ ...row, isAdditional: false }));
  }
  let baseId: number | null = null;
  if (!hasPriorBase && bases.length > 0) {
    baseId = [...bases].sort((a, b) => b.amount - a.amount || a.id - b.id)[0].id;
  }
  return bases.map((row) =>
    row.id === baseId
      ? { ...row, isAdditional: false }
      : { id: row.id, amount: Math.min(extra, row.amount), isAdditional: true },
  );
}

export function sumSelectedCategoryCosts(
  categories: Array<Pick<PublicCategoryLike, "id" | "registration_cost" | "effective_cost" | "has_own_cost">>,
  selectedIds: number[],
  entryFeeAmount: string | number | null | undefined,
  feeOption?: PublicFeeOptionLike | null,
): number {
  const byId = new Map(categories.map((category) => [category.id, category]));
  return selectedIds.reduce((total, id) => {
    const category = byId.get(id);
    return category ? total + effectiveCategoryCost(category, entryFeeAmount, feeOption) : total;
  }, 0);
}

// Mismos textos que valida el backend (division_age_restricted /
// division_gender_restricted) para que el jugador vea el motivo ANTES de
// enviar. Sin fecha de nacimiento aún tecleada no se acusa nada.
export function categoryEligibilityError(
  category: Pick<PublicCategoryLike, "name" | "gender" | "age_min" | "age_max">,
  birthdate: string,
  gender: string,
  tournamentYear: number,
): string | null {
  const birthYear = birthYearFromBirthdate(birthdate);
  const range = divisionBirthYearRange(category, tournamentYear);
  if (birthYear !== null && range && range.from !== null && birthYear < range.from) {
    return `La categoría ${category.name} acepta jugadores nacidos en ${range.from} o después.`;
  }
  if (birthYear !== null && range && range.to !== null && birthYear > range.to) {
    return `La categoría ${category.name} acepta jugadores nacidos en ${range.to} o antes.`;
  }
  if (category.gender === "male" && gender && gender !== "male") return `La categoría ${category.name} es varonil.`;
  if (category.gender === "female" && gender && gender !== "female") return `La categoría ${category.name} es femenil.`;
  return null;
}

// Motivo por el que el checkbox de una categoría aparece deshabilitado
// (null = seleccionable). El cupo lleno gana sobre la elegibilidad.
export function categoryDisabledReason(
  category: PublicCategoryLike,
  birthdate: string,
  gender: string,
  tournamentYear: number,
): string | null {
  if (category.is_full) return "Cupo lleno";
  if (category.is_closed) {
    return divisionClosedReasonText(category.closed_reason, category.effective_registration_deadline);
  }
  return categoryEligibilityError(category, birthdate, gender, tournamentYear);
}

// Gate QUINTO #129, B3: un perfil familiar YA inscrito solo debe VERSE (y
// comportarse) como deshabilitado cuando de verdad no puede agregar nada:
// cuando no queda NINGUNA categoría del torneo que (a) no tenga ya y (b) sea
// elegible para ÉL — edad/género según SUS datos, vía categoryDisabledReason,
// que además descarta el cupo lleno (una categoría llena tampoco es
// agregable). Sin fecha/género capturados no se acusa restricción (mismo
// criterio del selector), así que en la duda el perfil se ve habilitado.
export function profileHasEligibleAdditionalCategory(
  categories: PublicCategoryLike[],
  registeredDivisionIds: Iterable<number>,
  birthdate: string,
  gender: string,
  tournamentYear: number,
): boolean {
  const registered = new Set(registeredDivisionIds);
  return categories.some(
    (category) =>
      !registered.has(category.id) &&
      categoryDisabledReason(category, birthdate, gender, tournamentYear) === null,
  );
}

// Al recalcular elegibilidad (cambió fecha de nacimiento, género o el PERFIL
// familiar seleccionado) hay que soltar las categorías seleccionadas que
// dejaron de ser válidas. `registeredDivisionIds` (gate CUARTO #129, B5):
// categorías donde el perfil elegido YA está inscrito — su checkbox se
// deshabilita, así que si quedaran marcadas no habría forma de desmarcarlas
// y el submit terminaría en 409 division_already_registered.
export function pruneIneligibleSelections(
  categories: PublicCategoryLike[],
  selectedIds: number[],
  birthdate: string,
  gender: string,
  tournamentYear: number,
  registeredDivisionIds?: ReadonlySet<number>,
): number[] {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const next = selectedIds.filter((id) => {
    if (registeredDivisionIds?.has(id)) return false;
    const category = byId.get(id);
    return category !== undefined && categoryDisabledReason(category, birthdate, gender, tournamentYear) === null;
  });
  return next.length === selectedIds.length ? selectedIds : next;
}
